import { PositionedHtml } from "@/components/r3f/renderers/measurements/positioned-html";
import { RenderOrder } from "@/data-preparation-tool/utils/entity-utils";
import {
  Map2DControls,
  useThreeEventTarget,
} from "@faro-lotv/app-component-toolbox";
import { CursorStyle, FaroSvgIcon, useCustomCursor } from "@faro-lotv/flat-ui";
import { Map2DControls as Map2DControlsImpl } from "@faro-lotv/lotv";
import { Camera, useThree } from "@react-three/fiber";
import {
  Ref,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type JSX,
} from "react";
import { Matrix4, Vector3 } from "three";
import { ReactComponent as LocationSvg } from "../../icons/location.svg";
import {
  InteractionMode,
  SinglePinInteraction,
} from "./single-pin-interaction";

/** The size of the pin graphic. */
const PIN_SIZE = 75;

type SinglePinControlsProps = {
  /** Whether the pin manipulation is enabled. If false, only the map controls are active */
  isManipulationEnabled: boolean;

  /** Function to get the center of the object */
  getObjectCenter(): Vector3;

  /** The callback to execute when the object is manipulated by the user. */
  onTransform(transformChange: Matrix4): void;

  /** The callback to execute when the pin is dragged. */
  onDragStart?(mode: InteractionMode): void;

  /** The callback to execute when the pin is released. */
  onDragEnd?(mode: InteractionMode): void;

  /** The camera used for the controls */
  camera?: Camera;

  /** A ref for the map controls used with the pin controls */
  mapControlsRef?: Ref<Map2DControlsImpl>;
};

/** Imperative API for the controls available via the ref */
export type SinglePinControlsImperativeApi = {
  /** Re-centers the pin */
  recenterPin(): void;
};

/**
 * @returns A set of controls for the user to manipulate the transform of an object.
 *
 * The user can left-click on anywhere to place a pin.
 * - When dragging *on* the pin, the object is translated
 * - When dragging *around* the pin, the object is rotated.
 */
export const SinglePinControls = forwardRef<
  SinglePinControlsImperativeApi,
  SinglePinControlsProps
>(function SinglePinControls(
  {
    isManipulationEnabled,
    mapControlsRef,
    camera: cameraProp,
    getObjectCenter,
    onTransform,
    onDragStart,
    onDragEnd,
  },
  ref,
): JSX.Element {
  const defaultCamera = useThree((three) => three.camera);
  const camera = cameraProp ?? defaultCamera;

  const eventTarget = useThreeEventTarget();
  const eventTargetRef = useRef(eventTarget);

  const [pinInteraction] = useState(
    () => new SinglePinInteraction(camera, getObjectCenter),
  );
  pinInteraction.isManipulationEnabled = isManipulationEnabled;
  pinInteraction.getObjectCenter = getObjectCenter;

  const [pinPosition, setPinPosition] = useState(new Vector3());
  const [isPinVisible, setIsPinVisible] = useState(false);
  const [isOverPin, setIsOverPin] = useState(false);

  useImperativeHandle(
    ref,
    () => ({
      recenterPin() {
        const center = pinInteraction.centerPin();
        // For some reason, the `setPinEvent` is not received here, so the position needs to be updated manually
        setPinPosition(center);
      },
    }),
    [pinInteraction],
  );

  // Connect the callbacks to the interaction logic
  useEffect(() => {
    const disposeSetPin = pinInteraction.setPinEvent.on((newPinPosition) => {
      setIsPinVisible(true);
      setPinPosition((oldPosition) => oldPosition.copy(newPinPosition));
    });
    const disposeRemovePin = pinInteraction.removePinEvent.on(() => {
      setIsPinVisible(false);
    });
    const disposeTransform = pinInteraction.transformEvent.on(onTransform);
    const disposeMouseMoved = pinInteraction.mouseMovedEvent.on(
      (movedOverPin) => {
        if (isOverPin !== movedOverPin) {
          setIsOverPin(movedOverPin);
        }
      },
    );

    const disposeDragStart = pinInteraction.dragStartEvent.on(
      (mode: InteractionMode) => {
        onDragStart?.(mode);
      },
    );

    const disposeDragEnd = pinInteraction.dragEndEvent.on(
      (mode: InteractionMode) => {
        onDragEnd?.(mode);
      },
    );

    return () => {
      disposeSetPin.dispose();
      disposeRemovePin.dispose();
      disposeTransform.dispose();
      disposeMouseMoved.dispose();
      disposeDragEnd.dispose();
      disposeDragStart.dispose();
    };
  }, [pinInteraction, camera, onTransform, isOverPin, onDragStart, onDragEnd]);

  // Attach event listeners to the correct dom Element
  useEffect(() => {
    pinInteraction.attach(eventTarget);

    return () => {
      pinInteraction.detach();
    };
  }, [eventTarget, pinInteraction]);

  usePinAlignmentCursor(isManipulationEnabled, isPinVisible, isOverPin);

  return (
    <>
      <PositionedHtml
        center
        portal={eventTargetRef}
        position={pinPosition}
        renderOrder={RenderOrder.pin}
      >
        {isPinVisible && (
          <FaroSvgIcon
            source={LocationSvg}
            sx={{ width: PIN_SIZE, height: PIN_SIZE }}
          />
        )}
      </PositionedHtml>
      <Map2DControls
        ref={mapControlsRef}
        isPrimaryControl={!isManipulationEnabled}
      />
    </>
  );
});

function usePinAlignmentCursor(
  enable: boolean,
  isPinSet: boolean,
  isOverPin: boolean,
): void {
  let cursorToShow = CursorStyle.default;

  if (isPinSet) {
    cursorToShow = isOverPin ? CursorStyle.translation : CursorStyle.rotation;
  }

  const domElement = useThreeEventTarget();
  useCustomCursor({
    enable,
    cursorToShow,
    domElement,
  });
}
